import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBullseye,
  faGraduationCap,
  faClock,
  faUpDown,
  faFlagCheckered
} from '@/fontawesome';
import { TRADING_ROUTES } from '@/constants/routes';
import type {
  DashboardActivity,
  DashboardActivityType,
  DashboardActivityData,
  DashboardRecommendation,
  DashboardVideoProgress,
  ShortVideo
} from '@/modelsAPI';
import type {
  PreviewCoreRecommendation,
  PreviewEarningsRecommendation,
  PreviewLongTermRecommendation,
  PreviewSwingTradeRecommendation
} from '@/models';

// Core/Earnings previews share the same state enum and fields
type PreviewOptionRecommendation = PreviewCoreRecommendation | PreviewEarningsRecommendation;

// Swing/Long-Term previews share the same state enum and fields
type PreviewTradeRecommendation = PreviewSwingTradeRecommendation | PreviewLongTermRecommendation;

interface RecentActivityFeedProps {
  activities: DashboardActivity[];
}

const getActivityIcon = (type: DashboardActivityType) => {
  switch (type) {
    case 'CORE_RECOMMENDATION_CREATED':
    case 'EARNING_RECOMMENDATION_CREATED':
    case 'SWINGTRADE_RECOMMENDATION_CREATED':
    case 'LONGTERM_RECOMMENDATION_CREATED':
      return faBullseye;
    case 'SWINGTRADE_ADJUSTMENT_ADDED':
    case 'LONGTERM_ADJUSTMENT_ADDED':
      return faUpDown;
    case 'SWINGTRADE_RECOMMENDATION_FINISHED':
    case 'LONGTERM_RECOMMENDATION_FINISHED':
      return faFlagCheckered;
    case 'VIDEO_PUBLISHED':
      return faGraduationCap;
  }
};

const getActivityColor = (type: DashboardActivityType): string => {
  switch (type) {
    case 'CORE_RECOMMENDATION_CREATED':
    case 'EARNING_RECOMMENDATION_CREATED':
    case 'SWINGTRADE_RECOMMENDATION_CREATED':
    case 'LONGTERM_RECOMMENDATION_CREATED':
      return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/50';
    case 'SWINGTRADE_ADJUSTMENT_ADDED':
    case 'LONGTERM_ADJUSTMENT_ADDED':
      return 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/50';
    case 'SWINGTRADE_RECOMMENDATION_FINISHED':
    case 'LONGTERM_RECOMMENDATION_FINISHED':
      return 'text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/50';
    case 'VIDEO_PUBLISHED':
      return 'text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/50';
  }
};

const formatRelativeTime = (date: Date): string => {
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const hours = Math.floor(diff / (1000 * 60 * 60))
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))

  if (hours < 1) return 'gerade eben'
  if (hours === 1) return 'vor 1 Stunde'
  if (hours < 24) return `vor ${hours} Stunden`
  if (days === 1) return 'vor 1 Tag'
  if (days < 7) return `vor ${days} Tagen`
  if (days < 14) return 'vor 1 Woche'
  return `vor ${Math.floor(days / 7)} Wochen`;
};

// Type guards for activity data
const isRecommendation = (data: DashboardActivityData): data is DashboardRecommendation => {
  return 'state' in data && 'boost' in data;
};

const isOptionRecommendation = (data: DashboardActivityData): data is PreviewOptionRecommendation => {
  return isRecommendation(data) && 'winLossDelta' in data;
};

const isTradeRecommendation = (data: DashboardActivityData): data is PreviewTradeRecommendation => {
  return isRecommendation(data) && 'yield' in data;
};

const isVideo = (data: DashboardActivityData): data is ShortVideo => {
  return 'title' in data && 'courseID' in data && 'moduleID' in data && !('progress' in data);
};

const isVideoProgress = (data: DashboardActivityData): data is DashboardVideoProgress => {
  return 'progress' in data && 'videoTitle' in data;
};

// Get the link URL for an activity (returns null if not clickable)
const getActivityLink = (activity: DashboardActivity): string | null => {
  const { type, data } = activity;

  switch (type) {
    case 'CORE_RECOMMENDATION_CREATED':
      if (isRecommendation(data)) {
        return TRADING_ROUTES.CORE_RECOMMENDATION_DETAIL(data.id);
      }
      break;

    case 'EARNING_RECOMMENDATION_CREATED':
      if (isRecommendation(data)) {
        return TRADING_ROUTES.EARNINGS_RECOMMENDATION_DETAIL(data.id);
      }
      break;

    case 'SWINGTRADE_RECOMMENDATION_CREATED':
      if (isRecommendation(data)) {
        return TRADING_ROUTES.SWING_TRADE_DETAIL(data.id);
      }
      break;

    case 'LONGTERM_RECOMMENDATION_CREATED':
      if (isRecommendation(data)) {
        return TRADING_ROUTES.LONG_TERM_TRADE_DETAIL(data.id);
      }
      break;

    case 'VIDEO_PUBLISHED':
      if (isVideo(data)) {
        return `/courses/${data.courseID}/modules/${data.moduleID}/videos/${data.id}`;
      }
      break;

    case 'SWINGTRADE_ADJUSTMENT_ADDED':
      if (isRecommendation(data)) {
        return TRADING_ROUTES.SWING_TRADE_DETAIL(data.id);
      }
      break;

    case 'LONGTERM_ADJUSTMENT_ADDED':
      if (isRecommendation(data)) {
        return TRADING_ROUTES.LONG_TERM_TRADE_DETAIL(data.id);
      }
      break;

    case 'SWINGTRADE_RECOMMENDATION_FINISHED':
      if (isRecommendation(data)) {
        return TRADING_ROUTES.SWING_TRADE_DETAIL(data.id);
      }
      break;

    case 'LONGTERM_RECOMMENDATION_FINISHED':
      if (isRecommendation(data)) {
        return TRADING_ROUTES.LONG_TERM_TRADE_DETAIL(data.id);
      }
      break;
  }

  return null;
};

const getOptionStateLabel = (state: PreviewOptionRecommendation['state']): string => {
  switch (state) {
    case 'OPEN':
      return 'Offen';
    case 'FINISHED_ASSIGNED':
      return 'Eingebucht';
    case 'FINISHED_UNASSIGNED':
      return 'Erfolgreich beendet';
  }
};

const getTradeStateLabel = (state: PreviewTradeRecommendation['state']): string => {
  switch (state) {
    case 'OPEN':
      return 'Offen';
    case 'FINISHED_PROFIT':
      return 'Erfolgreich beendet';
    case 'FINISHED_LOSS':
      return 'Beendet';
  }
};

const formatYield = (yieldValue: number): string => {
  return ` (${yieldValue >= 0 ? '+' : ''}${(yieldValue * 100).toFixed(1)}%)`;
};

// Format activity data into a displayable string
const formatActivityData = (activity: DashboardActivity): string => {
  const { type, data } = activity;

  // Handle different activity types
  switch (type) {
    case 'CORE_RECOMMENDATION_CREATED':
      if (isOptionRecommendation(data)) {
        return `Neue Core-Empfehlung (${getOptionStateLabel(data.state)})${data.boost ? ' 🚀' : ''}`;
      }
      break;

    case 'EARNING_RECOMMENDATION_CREATED':
      if (isOptionRecommendation(data)) {
        return `Neue Earnings-Empfehlung (${getOptionStateLabel(data.state)})${data.boost ? ' 🚀' : ''}`;
      }
      break;

    case 'SWINGTRADE_RECOMMENDATION_CREATED':
      if (isTradeRecommendation(data)) {
        return `Neuer Swing Trade ${data.type} (${getTradeStateLabel(data.state)})${data.boost ? ' 🚀' : ''}`;
      }
      break;

    case 'LONGTERM_RECOMMENDATION_CREATED':
      if (isTradeRecommendation(data)) {
        return `Neuer Long-Term Trade ${data.type} (${getTradeStateLabel(data.state)})${data.boost ? ' 🚀' : ''}`;
      }
      break;

    case 'SWINGTRADE_ADJUSTMENT_ADDED':
      if (isRecommendation(data)) {
        return `Swing Trade: Einstieg hinzugefügt${data.boost ? ' 🚀' : ''}`;
      }
      break;

    case 'LONGTERM_ADJUSTMENT_ADDED':
      if (isRecommendation(data)) {
        return `Long-Term Trade: Einstieg hinzugefügt${data.boost ? ' 🚀' : ''}`;
      }
      break;

    case 'SWINGTRADE_RECOMMENDATION_FINISHED':
      if (isTradeRecommendation(data)) {
        return `Swing Trade abgeschlossen${formatYield(data.yield)}`;
      }
      break;

    case 'LONGTERM_RECOMMENDATION_FINISHED':
      if (isTradeRecommendation(data)) {
        return `Long-Term Trade abgeschlossen${formatYield(data.yield)}`;
      }
      break;

    case 'VIDEO_PUBLISHED':
      if (isVideo(data)) {
        return `Neues Video: ${data.title.trim()}`;
      }
      if (isVideoProgress(data)) {
        return `Video fortgesetzt: ${data.videoTitle.trim()}`;
      }
      break;
  }

  // Fallback: try to extract meaningful information
  if (typeof data === 'object' && data !== null) {
    if ('title' in data) {
      return data.title.trim();
    }
    if ('videoTitle' in data) {
      return data.videoTitle.trim();
    }
  }

  // Last resort
  return 'Neue Aktivität';
};

export default function RecentActivityFeed({ activities = [] }: RecentActivityFeedProps) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md border border-gray-200 dark:border-gray-700 h-full flex flex-col">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
          <FontAwesomeIcon icon={faClock} className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white">
          Letzte Aktivitäten
        </h3>
      </div>

      <div className="space-y-2 flex-1 overflow-y-auto pr-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        {activities.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
              Keine Aktivitäten vorhanden
            </p>
          </div>
        ) : (
          activities.map((activity, index) => {
            const link = getActivityLink(activity);
            const baseClassName = "flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group";
            const className = link !== null ? `${baseClassName} cursor-pointer` : baseClassName;

            const content = (
              <>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getActivityColor(activity.type)}`}>
                  <FontAwesomeIcon icon={getActivityIcon(activity.type)} className="w-5 h-5" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="flex-1 min-w-0 font-medium text-gray-900 dark:text-white line-clamp-1 text-left">
                      {formatActivityData(activity)}
                    </span>
                    <span className="text-gray-400 dark:text-gray-500 flex-shrink-0 whitespace-nowrap">
                      • {formatRelativeTime(new Date(activity.timestamp))}
                    </span>
                  </div>
                </div>
              </>
            );

            return link !== null ? (
              <Link key={index} to={link} className={className}>
                {content}
              </Link>
            ) : (
              <div key={index} className={className}>
                {content}
              </div>
            );
          })
        )}
      </div>
    </div>
  )
}
